/**
 * useFriendDexes — the trainer's friends plus each friend's caught dex rows.
 *
 * Several surfaces need the same thing: "for this species (in this variant),
 * which of my friends already has one?" — the Profile "Trades to chase" strip,
 * and the Wish List "who can help" section. Both need one
 * GET /api/friends/:id/dex per friend, which React Query dedupes across
 * components, and both need the fan-out bounded so a 200-friend account does
 * not fire 200 requests on page load.
 *
 * Friends pinned as PRIORITY friends (Friends > Manage, Pro) are
 * moved to the front of the list before the cap is applied, so a pinned friend
 * can never fall outside the probe window.
 *
 * A friend whose dex is private, empty or errored contributes an empty row list
 * — an unreadable dex must never surface phantom "they have it" claims — and
 * `pending` reports how many are still in flight so callers can say
 * "checking…" instead of "nobody has it".
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useSession } from "@/components/SessionProvider";
import {
  friendDisplayName,
  orderByPriority,
  priorityFriendIds,
} from "@/lib/priorityFriends";

/** One present entry in a friend's dex, as returned by /api/friends/:id/dex. */
export interface FriendDexRow {
  variant: string;
  speciesId: number;
  formId?: string | null;
}

/** A friends-list row from GET /api/friends. */
interface FriendsListRow {
  id: number;
  friend: {
    id: number;
    username: string | null;
    displayName: string | null;
    trainerName: string | null;
  } | null;
}

export interface FriendDex {
  friendId: number;
  friendName: string;
  /** True when this friend is one of the trainer's pinned priority friends. */
  isPriority: boolean;
  rows: readonly FriendDexRow[];
}

export interface UseFriendDexesResult {
  /** Friends whose dex has resolved (successfully or not). */
  dexes: FriendDex[];
  /** Every friend in the probe window, resolved or not — for empty states. */
  friendCount: number;
  /** True while the friends list itself is loading. */
  isLoadingFriends: boolean;
  /** How many probed friends have not resolved yet. */
  pending: number;
}

/**
 * Default probe cap. Each probed friend costs one /api/friends/:id/dex request
 * on mount, so this bounds the fan-out for a large friends list. Callers that
 * only need a handful of leads can pass a smaller cap.
 */
export const DEFAULT_FRIEND_DEX_CAP = 12;

export function useFriendDexes(cap: number = DEFAULT_FRIEND_DEX_CAP): UseFriendDexesResult {
  const { userPriorities } = useSession();
  const pinnedIds = useMemo(() => priorityFriendIds(userPriorities), [userPriorities]);

  const friendsQ = useQuery<{ friends: FriendsListRow[] }>({
    queryKey: ["/api/friends"],
    staleTime: 60_000,
  });

  // Pinned friends first, then the rest in server order, then cap.
  const pool = useMemo(() => {
    const rows = (friendsQ.data?.friends ?? []).filter((r) => r.friend?.id != null);
    return orderByPriority(rows, (r) => r.friend!.id, pinnedIds).slice(0, Math.max(0, cap));
  }, [friendsQ.data, pinnedIds, cap]);

  const results = useQueries({
    queries: pool.map((r) => ({
      queryKey: [`/api/friends/${r.friend!.id}/dex`],
      staleTime: 60_000,
    })),
  });

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  // Identity of the RESOLVED query set. `results` is a fresh array on every
  // render, so memoising on it would be pointless; memoising on status alone
  // would miss a refetch that changed the rows. The update stamps move whenever
  // a query settles OR re-settles, which is exactly when the dexes change.
  const resultsKey = results
    .map((r) => `${r.status}:${r.dataUpdatedAt}:${r.errorUpdatedAt}`)
    .join("|");

  const dexes = useMemo(() => {
    const out: FriendDex[] = [];
    pool.forEach((row, i) => {
      const q = results[i];
      if (!q || (!q.isSuccess && !q.isError)) return; // still in flight
      const data = q.data as { private?: boolean; rows?: FriendDexRow[] } | undefined;
      const rows = data && !data.private && Array.isArray(data.rows) ? data.rows : [];
      out.push({
        friendId: row.friend!.id,
        friendName: friendDisplayName(row.friend),
        isPriority: pinnedSet.has(row.friend!.id),
        rows,
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, pinnedSet, resultsKey]);

  return {
    dexes,
    friendCount: pool.length,
    isLoadingFriends: friendsQ.isLoading,
    pending: Math.max(0, pool.length - dexes.length),
  };
}
